"use client";

import React from "react";
import { ArrowRight, GraduationCap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const member = {
  name: "Dr. Samuel L. Sharmat",
  role: "Lead Psychiatrist",
  image: "https://vast-cheetah.10web.cloud/wp-content/uploads/2026/05/samuel-sharmat-color-1365.png?v=h9utcntds7u",
  education: [{
    degree: "Addiction Psychiatry Fellowship",
    school: "The Icahn School of Medicine at Mount Sinai"
  }, {
    degree: "Psychiatry Residency",
    school: "The Icahn School of Medicine at Mount Sinai"
  }, {
    degree: "Doctor of Medicine (MD)",
    school: "St. George's University"
  }]
};
export default function TheCareTeam() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="dr-sharmat" className="w-full" ref={ref}>
      <div className="w-full py-20 lg:py-32 bg-background">
        <div className="container mx-auto px-6 lg:px-12 flex flex-col lg:flex-row gap-12 lg:gap-20 items-start">

          <div className={`w-full lg:w-[40%] relative aspect-[3/4] lg:aspect-[4/5] overflow-hidden rounded-xl shadow-xl transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <div className="absolute inset-0 bg-primary/10 mix-blend-multiply z-10 pointer-events-none" />
            <img src={member.image} alt={member.name} className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-700 ease-in-out scale-100 hover:scale-105" />
          </div>

          <div className={`w-full lg:w-[60%] flex flex-col gap-8 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"}`}>
            <div className="space-y-2">
              <h2 className="text-4xl lg:text-5xl font-serif text-foreground tracking-tight">
                {member.name}
              </h2>
              <p className="text-lg text-muted-foreground font-medium uppercase tracking-wide">
                {member.role}
              </p>
            </div>

            <div className="flex flex-col gap-4">
              <p className="text-lg font-default text-foreground/80 leading-relaxed">
                The path to effective mental health care is often a grueling marathon of trial and error. For many, the "medication merry-go-round" lasts months—or even decades—leaving patients exhausted and demoralized. Early in my career, I realized that the standard of care was simply not fast or precise enough for the level of suffering I witnessed. Driven by an empathic need to find a better way, I developed a custom clinical interview process designed to bypass surface symptoms and reach the core of a patient’s experience. When I integrated this approach with advanced genetic testing, the results were transformative: patients weren't just getting better; they were getting better at a speed I had never seen in traditional practice.
              </p>
              <p className="text-lg font-default text-foreground/80 leading-relaxed">
                My diagnostic philosophy was forged at St. George’s University, where I was trained as a "doctor detective." This mindset was further sharpened during a Family Practice internship, which instilled in me a deep commitment to treating the whole person. Rather than viewing psychiatry in a vacuum, I look at the intricate interplay between a patient’s biological systems and their psychosocial environment—home, work, and community. By viewing the patient through a comprehensive lens, I can identify the "hidden" factors that often masquerade as purely psychiatric issues.
              </p>
              <p className="text-lg font-default text-foreground/80 leading-relaxed">
                In my practice today, I utilize psychiatric genetics to peer under the hood of the brain’s machinery. By testing every patient, I can identify specific variations in how their brain manufactures essential chemicals, builds structures, and processes reward and attention. This data turns the "invisible" into the visible. For many of my patients—particularly those in mid-to-late life—this discovery is a moment of profound validation. It replaces years of trying to "snap out of it" with a clear biological understanding, often bringing tears of relief as they realize their struggle was never a failure of will, but a treatable genetic variation.
              </p>
              <p className="text-lg font-default text-foreground/80 leading-relaxed">
                Beyond the clinic, I believe in the restorative power of connection and story. Whether I’m jamming with a "dad-band," exploring an old café in a foreign city, or losing myself in a piece of award-winning literature, I am constantly reminded of the richness of the human experience. My mission is to help my patients strip away the weight of untreated conditions so they can return to their own lives—their families, their hobbies, and their passions—with a renewed sense of clarity and joy.
              </p>
            </div>

            <div className="space-y-4">
              <h4 className="text-xs font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                <GraduationCap className="w-4 h-4 text-primary" />
                Education & Credentials
              </h4>
              <ul className="relative border-l border-border ml-2 pl-4 space-y-4">
                {member.education.map((edu, i) => <li key={i} className="relative">
                    <span className="absolute -left-[21px] top-1.5 w-2.5 h-2.5 rounded-full border-2 border-background bg-primary shrink-0" />
                    <div className="flex flex-col space-y-0.5">
                      <span className="text-sm font-semibold text-foreground tracking-tight">{edu.degree}</span>
                      <span className="text-xs text-muted-foreground">{edu.school}</span>
                    </div>
                  </li>)}
              </ul>
            </div>

            <div className={`transition-all duration-1000 delay-400 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
              <Button asChild size="lg" className="bg-[var(--ring)] hover:bg-[var(--ring)]/90 text-white border-none shadow-lg hover:shadow-xl transition-all h-14 px-8 rounded-full text-lg font-medium">
                <Link to="https://sharmatpsychiatricgroup.com/contact/">
                  Book a Consultation
                  <ArrowRight className="ml-2 size-5" />
                </Link>
              </Button>
            </div>
          </div>

        </div>
      </div>
    </section>;
}